import { useState, useEffect, useRef } from 'react';
import UserAlert from 'models/UserAlert';
import useApiCall from './ApiCall';
import userAuthService from 'services/userAuthService';
import { useUserContext } from './UserContext';
import UserAlertPopup from 'view/components/common/UserAlertPopup';
import { useLocation } from 'react-router-dom';

const useUserAlerts = (path?: string) => {
  const [alerts, setAlerts] = useState<UserAlert[]>([]);
  const { user } = useUserContext();
  const [activeAlert, setActiveAlert] = useState<UserAlert | null>();
  const makeApiCall = useApiCall();
  const location = useLocation();
  const previousPathRef = useRef<string>("");
  const fetchedPathsRef = useRef<Set<string>>(new Set());

  // Only fetch alerts when user is logged in and when visiting a new pathname
  useEffect(() => {
    if (!user?.id) return;

    // Extract main path segments (e.g., /teacher/classroom/12345 -> teacher/classroom)
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const mainPath = pathSegments.length > 0 ? `/${pathSegments[0]}/${pathSegments[1] || ''}` : '/';

    // Only fetch if we haven't fetched for this main path before
    if (!fetchedPathsRef.current.has(mainPath)) {
      fetchedPathsRef.current.add(mainPath);

      makeApiCall(userAuthService.getUserAlerts)
        .then((respAlerts) => setAlerts(respAlerts))
        .catch((error) => console.error(error));
    }

    previousPathRef.current = location.pathname;
  }, [user?.id, location.pathname, makeApiCall]);

  const showAlertPopup = (alert: UserAlert) => {
    setActiveAlert(alert);
  };

  const handleAlertModalClosed = () => {
    if (!activeAlert) return;

    setAlerts((prev) => prev.filter((a) => a !== activeAlert));
    setActiveAlert(null);

    makeApiCall(userAuthService.acknowledgeUserAlert, activeAlert.id)
      .catch((error) => console.error(error));
  };

  const UserAlertModal = () =>
    activeAlert ? (
      <UserAlertPopup alert={activeAlert} onClose={handleAlertModalClosed} />
    ) : null;

  // Added a function to reset the fetched paths cache if needed
  const resetFetchedPaths = () => {
    fetchedPathsRef.current.clear();
  };

  return {
    alerts,
    setAlerts,
    showAlertPopup,
    UserAlertModal,
    resetFetchedPaths
  };
};

export default useUserAlerts;
